import { faqs } from './faqs'
import { faqsHow } from './faqsHow'
import { faqsContact } from './faqsContact'
import { flatten } from '../utils/reactText'
import { PUBLISHED, findPost } from './posts'
import type { FaqItem } from './faqs'

export const SITE_ORIGIN = 'https://meetsynthia.ai'
/**
 * The social card shown when a link to the site is pasted anywhere. Exported
 * from /og and hosted on the CDN, so it doesn't depend on a file at the domain
 * root. Articles override this with their own hero art.
 */
export const OG_IMAGE = 'https://cdn.magicpatterns.com/uploads/9UDNAhjgbvvvsFYzgNUGqk/og.png'

const ORGANIZATION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Synthia',
  url: SITE_ORIGIN,
  email: 'hello@meetsynthia.ai',
  description:
    "Synthia is the instruction governance layer for enterprise AI: obligations atomized into binary DO/DON'T instruction data, composed so what's binding goes first, with a receipt for what the model was told.",
}

const SOFTWARE_APPLICATION = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Synthia',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: SITE_ORIGIN,
  description:
    "Instruction governance for enterprise AI. Synthia atomizes obligations into binary DO/DON'T instruction data, seats what's binding first, enforces constitutional instructions on every model call, and signs a receipt for what the model was told.",
}

function faqPage(items: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: flatten(item.a) },
    })),
  }
}

export type PageSeo = {
  path: string
  title: string
  description: string
  ogTitle?: string
  ogDescription?: string
  ogType?: string
  /** Overrides the site-wide social card, e.g. an article's own hero art. */
  image?: string
  jsonLd: unknown[]
}

export const PAGE_SEO: Record<string, PageSeo> = {
  '/': {
    path: '/',
    title: 'Synthia — Instruction Governance for Enterprise AI',
    description:
      'Your best judgment, built into every answer that matters. Synthia turns your policies, regulatory requirements, and industry guidance into instruction data any model can act on before it answers — with a receipt for what the model was told.',
    ogDescription:
      "What's binding goes first. Obligations atomized into binary DO/DON'T instruction data, seated before the model runs, with a signed receipt.",
    jsonLd: [ORGANIZATION, SOFTWARE_APPLICATION, faqPage(faqs)],
  },
  '/how': {
    path: '/how',
    title: 'How Synthia Works — Atomize, Compose, Receipt',
    description:
      "How Synthia works: atomize obligations into binary DO/DON'T instruction data, seat what's binding first, enforce constitutional instructions on every pass, and sign a receipt for what the model was told.",
    ogDescription:
      'Atomize, compose, receipt — plus the constitutional tier: instructions no prompt can waive, checked before every model call.',
    jsonLd: [ORGANIZATION, faqPage(faqsHow)],
  },
  '/contact': {
    path: '/contact',
    title: 'Talk to Synthia — Bring Your Highest Standards',
    description:
      "Bring your highest standards. Tell us your most challenging workflow and we'll show your company's judgment turned into instruction data — governed, receipted, and owned by you.",
    ogDescription:
      'One workflow in. Your library of instruction data started — owned, governed, receipted.',
    jsonLd: [ORGANIZATION, faqPage(faqsContact)],
  },
  '/blog': {
    path: '/blog',
    title: 'Signals — Field Notes from the Instruction Layer',
    description:
      'What we’re learning about governing what AI models are told — the moment before the answer, where the obligations either rode or they didn’t.',
    ogDescription: 'Field notes from the instruction layer.',
    jsonLd: [
      ORGANIZATION,
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Signals',
        url: `${SITE_ORIGIN}/blog`,
        publisher: { '@type': 'Organization', name: 'Synthia', url: SITE_ORIGIN },
        blogPost: PUBLISHED.map((post) => ({
          '@type': 'BlogPosting',
          headline: post.title,
          datePublished: post.date,
          author: { '@type': 'Person', name: post.author },
          url: `${SITE_ORIGIN}/blog/${post.slug}`,
        })),
      },
    ],
  },
}

/**
 * Resolves the head data for a path, including the per-article pages that can't
 * be enumerated in the static map above. Each post gets its own title,
 * description, canonical, and BlogPosting entity so it stands alone in search
 * and in answer engines.
 */
export function seoFor(pathname: string): PageSeo {
  const direct = PAGE_SEO[pathname]
  if (direct) return direct

  const match = pathname.match(/^\/blog\/(.+)$/)
  const post = match ? findPost(match[1]) : undefined
  if (post) {
    return {
      path: `/blog/${post.slug}`,
      title: `${post.title} — Synthia`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogType: 'article',
      image: post.hero,
      jsonLd: [
        ORGANIZATION,
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          datePublished: post.date,
          author: { '@type': 'Person', name: post.author },
          publisher: { '@type': 'Organization', name: 'Synthia', url: SITE_ORIGIN },
          mainEntityOfPage: `${SITE_ORIGIN}/blog/${post.slug}`,
          articleSection: post.category,
          ...(post.hero ? { image: post.hero } : {}),
        },
      ],
    }
  }

  return PAGE_SEO['/']
}
